import React, { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Head, router } from '@inertiajs/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/useToast';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { DocumentStatusBadge } from '@/components/documents/DocumentStatusBadge';
import { PaginatedTableControls } from '@/components/ui/paginated-table';
import { emptyPagination, fetchJson, PaginationMeta, RequestSequencer } from '@/lib/jsonPagination';
import { cn } from '@/lib/utils';
import { buildChildrenByParentId } from '@/lib/documentGroupTree';
import { ChevronDown, ChevronRight, Edit, FileText, FolderOpen, GripVertical, Loader2, Plus, Search, Trash2 } from 'lucide-react';

interface Document {
  id: number;
  name: string;
  description?: string;
  status: string;
}

interface AssignableDocument extends Document {
  assigned_to_group: boolean;
}

interface DocumentGroup {
  id: number;
  name: string;
  description?: string;
  position: number;
  parent_id?: number | null;
  documents_count: number;
}

interface Municipality {
  id: number;
  name: string;
}

interface Props {
  municipality: Municipality;
  document_groups: DocumentGroup[];
  ungrouped_documents_count: number;
}

interface DocumentsState {
  documents: Document[];
  pagination: PaginationMeta;
  search: string;
  loading: boolean;
}

interface AssignableDocumentsState {
  documents: AssignableDocument[];
  pagination: PaginationMeta;
  search: string;
  loading: boolean;
}

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

function groupRequestSequencer(
  sequencers: MutableRefObject<Map<number, RequestSequencer>>,
  groupId: number
) {
  let sequencer = sequencers.current.get(groupId);
  if (!sequencer) {
    sequencer = new RequestSequencer();
    sequencers.current.set(groupId, sequencer);
  }
  return sequencer;
}

function descendantIdSet(groups: DocumentGroup[], rootId: number): Set<number> {
  const childrenByParent = buildChildrenByParentId(groups);
  const out = new Set<number>();
  const walk = (id: number) => {
    for (const child of childrenByParent.get(id) ?? []) {
      out.add(child.id);
      walk(child.id);
    }
  };
  walk(rootId);
  return out;
}

function formatServerValidationErrors(errors: unknown, fallback: string): string {
  if (Array.isArray(errors)) {
    const messages = errors.filter((error): error is string => typeof error === 'string' && error.length > 0);
    return messages.length > 0 ? messages.join(' ') : fallback;
  }
  if (errors && typeof errors === 'object') {
    const messages = Object.values(errors as Record<string, unknown>).flatMap((value) => {
      if (typeof value === 'string' && value.length > 0) return [value];
      if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
      return [];
    });
    return messages.length > 0 ? messages.join(' ') : fallback;
  }
  return fallback;
}

function buildParentOptions(groups: DocumentGroup[], excludeIds: Set<number>): { id: number; label: string }[] {
  const childrenByParent = buildChildrenByParentId(groups);
  const result: { id: number; label: string }[] = [];
  const walk = (parentId: number | null, depth: number) => {
    for (const group of childrenByParent.get(parentId) ?? []) {
      if (excludeIds.has(group.id)) continue;
      result.push({ id: group.id, label: `${'- '.repeat(depth)}${group.name}` });
      walk(group.id, depth + 1);
    }
  };
  walk(null, 0);
  return result;
}

type DocumentListProps = {
  state?: DocumentsState;
  searchPlaceholder: string;
  onSearchChange: (search: string) => void;
  onSearchSubmit: () => void;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
};

function DocumentList({ state, searchPlaceholder, onSearchChange, onSearchSubmit, onPageChange, onPerPageChange }: DocumentListProps) {
  const currentState = state ?? {
    documents: [],
    pagination: emptyPagination(),
    search: '',
    loading: false
  };

  return (
    <div className="space-y-3">
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          onSearchSubmit();
        }}
      >
        <Input
          className="min-w-0 flex-1"
          value={currentState.search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
        />
        <Button type="submit" variant="outline" className="shrink-0">
          <Search className="h-4 w-4" />
        </Button>
      </form>

      {currentState.loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading documents
        </div>
      ) : currentState.documents.length === 0 ? (
        <p className="text-sm text-muted-foreground">No documents found.</p>
      ) : (
        <div className="grid gap-2">
          {currentState.documents.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between rounded-md border border-border/60 bg-background/50 p-2">
              <div className="min-w-0 pr-2">
                <p className="truncate font-medium">{doc.name}</p>
                <p className="truncate text-sm text-muted-foreground">{doc.description}</p>
              </div>
              <DocumentStatusBadge status={doc.status} />
            </div>
          ))}
        </div>
      )}

      {!currentState.loading && (
        <PaginatedTableControls
          currentPage={currentState.pagination.current_page}
          totalPages={currentState.pagination.total_pages}
          perPage={currentState.pagination.per_page}
          totalCount={currentState.pagination.total_count}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
        />
      )}
    </div>
  );
}

type GroupBranchProps = {
  group: DocumentGroup;
  depth: number;
  childrenByParent: Map<number | null, DocumentGroup[]>;
  expandedGroupIds: Set<number>;
  documentsByGroup: Record<number, DocumentsState>;
  onToggleDocuments: (groupId: number) => void;
  onDocumentSearchChange: (groupId: number, search: string) => void;
  onDocumentSearchSubmit: (groupId: number) => void;
  onDocumentPageChange: (groupId: number, page: number) => void;
  onDocumentPerPageChange: (groupId: number, perPage: number) => void;
  onEdit: (group: DocumentGroup) => void;
  onAssign: (group: DocumentGroup) => void;
  onDelete: (id: number) => void;
};

function DocumentGroupBranch({
  group,
  depth,
  childrenByParent,
  expandedGroupIds,
  documentsByGroup,
  onToggleDocuments,
  onDocumentSearchChange,
  onDocumentSearchSubmit,
  onDocumentPageChange,
  onDocumentPerPageChange,
  onEdit,
  onAssign,
  onDelete,
}: GroupBranchProps) {
  const childGroups = childrenByParent.get(group.id) ?? [];
  const isNested = depth > 0;
  const isExpanded = expandedGroupIds.has(group.id);

  return (
    <div className={cn(!isNested && 'mb-6 last:mb-0')}>
      <Card className={cn('overflow-hidden', isNested && 'border-border/50 bg-muted/20 shadow-none', !isNested && 'shadow-sm')}>
        <CardHeader className={cn(isNested && 'pb-3 pt-4')}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <GripVertical className={cn('h-5 w-5 shrink-0 text-muted-foreground', isNested ? 'mt-0.5' : 'mt-1')} />
              <div className="min-w-0">
                <CardTitle className={cn('flex items-center gap-2', isNested ? 'text-base font-semibold' : 'text-lg')}>
                  <FolderOpen className={cn('shrink-0', isNested ? 'h-4 w-4' : 'h-5 w-5')} />
                  <span className="truncate">{group.name}</span>
                </CardTitle>
                <CardDescription className={cn(isNested && 'text-xs')}>
                  {group.description}
                </CardDescription>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:shrink-0 sm:justify-end">
              <Button variant="outline" size="sm" onClick={() => onAssign(group)}>
                <FileText className="mr-1 h-4 w-4" />
                Assign
              </Button>
              <Button variant="outline" size="sm" onClick={() => onEdit(group)}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => onDelete(group.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className={cn(isNested && 'pt-0')}>
          <Button variant="ghost" size="sm" className="-ml-2" onClick={() => onToggleDocuments(group.id)}>
            {isExpanded ? <ChevronDown className="mr-1 h-4 w-4" /> : <ChevronRight className="mr-1 h-4 w-4" />}
            {group.documents_count} document{group.documents_count !== 1 ? 's' : ''}
          </Button>

          {isExpanded && (
            <div className="mt-3">
              <DocumentList
                state={documentsByGroup[group.id]}
                searchPlaceholder="Search documents in this group"
                onSearchChange={(search) => onDocumentSearchChange(group.id, search)}
                onSearchSubmit={() => onDocumentSearchSubmit(group.id)}
                onPageChange={(page) => onDocumentPageChange(group.id, page)}
                onPerPageChange={(perPage) => onDocumentPerPageChange(group.id, perPage)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {childGroups.length > 0 && (
        <div className="mt-2 ml-2 space-y-2 border-l border-border/60 pl-3">
          {childGroups.map((child) => (
            <DocumentGroupBranch
              key={child.id}
              group={child}
              depth={depth + 1}
              childrenByParent={childrenByParent}
              expandedGroupIds={expandedGroupIds}
              documentsByGroup={documentsByGroup}
              onToggleDocuments={onToggleDocuments}
              onDocumentSearchChange={onDocumentSearchChange}
              onDocumentSearchSubmit={onDocumentSearchSubmit}
              onDocumentPageChange={onDocumentPageChange}
              onDocumentPerPageChange={onDocumentPerPageChange}
              onEdit={onEdit}
              onAssign={onAssign}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Index({ municipality, document_groups, ungrouped_documents_count }: Props) {
  const { toast } = useToast();
  const [groups, setGroups] = useState<DocumentGroup[]>(document_groups);
  const [ungroupedCount, setUngroupedCount] = useState(ungrouped_documents_count);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<number>>(new Set());
  const [documentsByGroup, setDocumentsByGroup] = useState<Record<number, DocumentsState>>({});
  const [ungroupedExpanded, setUngroupedExpanded] = useState(false);
  const [ungroupedDocuments, setUngroupedDocuments] = useState<DocumentsState>({
    documents: [],
    pagination: emptyPagination(),
    search: '',
    loading: false
  });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<DocumentGroup | null>(null);
  const [assignableDocuments, setAssignableDocuments] = useState<AssignableDocumentsState>({
    documents: [],
    pagination: emptyPagination(),
    search: '',
    loading: false
  });
  const groupSequencers = useRef(new Map<number, RequestSequencer>());
  const ungroupedSequencer = useRef(new RequestSequencer());
  const assignableSequencer = useRef(new RequestSequencer());
  const [assigningDocumentIds, setAssigningDocumentIds] = useState<Set<number>>(new Set());
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    position: 0,
    parent_id: null as number | null,
  });
  const [assignAllUngroupedDocuments, setAssignAllUngroupedDocuments] = useState(false);

  useEffect(() => {
    setGroups(document_groups);
    setUngroupedCount(ungrouped_documents_count);
  }, [document_groups, ungrouped_documents_count]);

  const childrenByParent = useMemo(() => buildChildrenByParentId(groups), [groups]);
  const rootGroups = childrenByParent.get(null) ?? [];

  const createParentOptions = useMemo(() => buildParentOptions(groups, new Set()), [groups]);

  const editParentOptions = useMemo(() => {
    if (!selectedGroup) return [];
    const excludedIds = new Set<number>([selectedGroup.id]);
    descendantIdSet(groups, selectedGroup.id).forEach((id) => excludedIds.add(id));
    return buildParentOptions(groups, excludedIds);
  }, [groups, selectedGroup]);

  const updateGroupDocumentCount = (groupId: number, documentsCount: number) => {
    setGroups((currentGroups) => currentGroups.map((group) => (
      group.id === groupId ? { ...group, documents_count: documentsCount } : group
    )));
  };

  const loadGroupDocuments = async (
    groupId: number,
    options: { page?: number; perPage?: number; search?: string } = {}
  ) => {
    const currentState = documentsByGroup[groupId];
    const page = options.page || 1;
    const perPage = options.perPage || currentState?.pagination.per_page || 25;
    const search = options.search ?? currentState?.search ?? '';
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    if (search.trim()) params.set('search', search.trim());

    setDocumentsByGroup((previous) => ({
      ...previous,
      [groupId]: {
        documents: previous[groupId]?.documents || [],
        pagination: previous[groupId]?.pagination || emptyPagination(),
        search,
        loading: true
      }
    }));

    const request = groupRequestSequencer(groupSequencers, groupId).start();
    const result = await fetchJson<{ documents: Document[]; pagination: PaginationMeta }>(
      `/municipalities/${municipality.id}/document_groups/${groupId}/documents.json?${params.toString()}`
    );

    if (!result.ok) {
      if (!request.isCurrent()) return;

      toast({ title: 'Error', description: 'Failed to load group documents.', variant: 'destructive' });
      setDocumentsByGroup((previous) => ({
        ...previous,
        [groupId]: {
          documents: previous[groupId]?.documents || [],
          pagination: previous[groupId]?.pagination || emptyPagination(),
          search,
          loading: false
        }
      }));
      return;
    }

    if (!request.isCurrent()) return;

    setDocumentsByGroup((previous) => ({
      ...previous,
      [groupId]: {
        documents: result.data.documents,
        pagination: result.data.pagination,
        search,
        loading: false
      }
    }));
  };

  const loadUngroupedDocuments = async (options: { page?: number; perPage?: number; search?: string } = {}) => {
    const page = options.page || 1;
    const perPage = options.perPage || ungroupedDocuments.pagination.per_page || 25;
    const search = options.search ?? ungroupedDocuments.search;
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    if (search.trim()) params.set('search', search.trim());

    setUngroupedDocuments((previous) => ({ ...previous, search, loading: true }));

    const request = ungroupedSequencer.current.start();
    const result = await fetchJson<{ documents: Document[]; pagination: PaginationMeta }>(
      `/municipalities/${municipality.id}/document_groups/ungrouped_documents.json?${params.toString()}`
    );

    if (!result.ok) {
      if (!request.isCurrent()) return;

      toast({ title: 'Error', description: 'Failed to load ungrouped documents.', variant: 'destructive' });
      setUngroupedDocuments((previous) => ({ ...previous, search, loading: false }));
      return;
    }

    if (!request.isCurrent()) return;

    setUngroupedDocuments({
      documents: result.data.documents,
      pagination: result.data.pagination,
      search,
      loading: false
    });
  };

  const loadAssignableDocuments = async (
    options: { page?: number; perPage?: number; search?: string; group?: DocumentGroup } = {}
  ) => {
    const group = options.group || selectedGroup;
    if (!group) return;

    const page = options.page || 1;
    const perPage = options.perPage || assignableDocuments.pagination.per_page || 25;
    const search = options.search ?? assignableDocuments.search;

    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    if (search.trim()) params.set('search', search.trim());

    setAssignableDocuments((previous) => ({ ...previous, search, loading: true }));

    const request = assignableSequencer.current.start();
    const result = await fetchJson<{
      documents: AssignableDocument[];
      pagination: PaginationMeta;
    }>(`/municipalities/${municipality.id}/document_groups/${group.id}/assignable_documents.json?${params.toString()}`);

    if (!result.ok) {
      if (!request.isCurrent()) return;

      toast({ title: 'Error', description: 'Failed to load assignable documents.', variant: 'destructive' });
      setAssignableDocuments((previous) => ({ ...previous, search, loading: false }));
      return;
    }

    if (!request.isCurrent()) return;

    setAssignableDocuments({
      documents: result.data.documents,
      pagination: result.data.pagination,
      search,
      loading: false
    });
  };

  const handleDocumentAssignment = async (documentId: number, assigned: boolean) => {
    if (!selectedGroup) return;
    setAssigningDocumentIds((previous) => new Set(previous).add(documentId));

    const response = await fetch(`/municipalities/${municipality.id}/document_groups/${selectedGroup.id}/document_assignment`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify({ document_id: documentId, assigned })
    });

    setAssigningDocumentIds((previous) => {
      const next = new Set(previous);
      next.delete(documentId);
      return next;
    });

    if (!response.ok) {
      toast({ title: 'Error', description: 'Failed to update document assignment.', variant: 'destructive' });
      return;
    }

    const data = await response.json();
    updateGroupDocumentCount(selectedGroup.id, data.documents_count);
    if (data.previous_group_id && data.previous_group_documents_count !== null) {
      updateGroupDocumentCount(data.previous_group_id, data.previous_group_documents_count);
    }
    setUngroupedCount(data.ungrouped_documents_count);
    setAssignableDocuments((previous) => ({
      ...previous,
      documents: previous.documents.map((doc) => (
        doc.id === documentId ? { ...doc, assigned_to_group: data.assigned } : doc
      ))
    }));

    if (expandedGroupIds.has(selectedGroup.id)) {
      await loadGroupDocuments(selectedGroup.id, { page: documentsByGroup[selectedGroup.id]?.pagination.current_page || 1 });
    }
    if (data.previous_group_id && expandedGroupIds.has(data.previous_group_id)) {
      await loadGroupDocuments(data.previous_group_id, { page: documentsByGroup[data.previous_group_id]?.pagination.current_page || 1 });
    }
    if (ungroupedExpanded) {
      await loadUngroupedDocuments({ page: ungroupedDocuments.pagination.current_page });
    }
  };

  const handleCreate = (event: React.FormEvent) => {
    event.preventDefault();
    router.post(`/municipalities/${municipality.id}/document_groups`, {
      municipality_document_group: formData,
      assign_all_ungrouped_documents: assignAllUngroupedDocuments
    }, {
      onSuccess: () => {
        setIsCreateDialogOpen(false);
        setFormData({ name: '', description: '', position: 0, parent_id: null });
        setAssignAllUngroupedDocuments(false);
        toast({ title: 'Success', description: 'Document group created successfully' });
      },
      onError: (errors) => {
        toast({
          title: 'Error',
          description: formatServerValidationErrors(errors, 'Failed to create document group'),
          variant: 'destructive',
        });
      },
    });
  };

  const handleUpdate = (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedGroup) return;

    router.patch(`/municipalities/${municipality.id}/document_groups/${selectedGroup.id}`, {
      municipality_document_group: formData
    }, {
      onSuccess: () => {
        setIsEditDialogOpen(false);
        setSelectedGroup(null);
        toast({ title: 'Success', description: 'Document group updated successfully' });
      },
      onError: (errors) => {
        toast({
          title: 'Error',
          description: formatServerValidationErrors(errors, 'Failed to update document group'),
          variant: 'destructive',
        });
      },
    });
  };

  const handleDelete = (groupId: number) => {
    if (!confirm('Are you sure you want to delete this group? Documents will be ungrouped but not deleted.')) return;

    router.delete(`/municipalities/${municipality.id}/document_groups/${groupId}`, {
      onSuccess: () => {
        toast({ title: 'Success', description: 'Document group deleted successfully' });
      }
    });
  };

  const toggleGroupDocuments = (groupId: number) => {
    const isExpanded = expandedGroupIds.has(groupId);
    setExpandedGroupIds((previous) => {
      const next = new Set(previous);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });

    if (!isExpanded && !documentsByGroup[groupId]) {
      loadGroupDocuments(groupId);
    }
  };

  const openEditDialog = (group: DocumentGroup) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      position: group.position,
      parent_id: group.parent_id ?? null,
    });
    setIsEditDialogOpen(true);
  };

  const openAssignDialog = (group: DocumentGroup) => {
    setSelectedGroup(group);
    setAssignableDocuments({
      documents: [],
      pagination: emptyPagination(),
      search: '',
      loading: true
    });
    setIsAssignDialogOpen(true);
    loadAssignableDocuments({ group, search: '', page: 1 });
  };

  return (
    <>
      <Head title={`Document Groups - ${municipality.name}`} />

      <div className="container mx-auto max-w-7xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{municipality.name}</h1>
            <p className="text-muted-foreground">Manage document groups</p>
          </div>

          <Dialog
            open={isCreateDialogOpen}
            onOpenChange={(open) => {
              setIsCreateDialogOpen(open);
              if (open) {
                setFormData({ name: '', description: '', position: 0, parent_id: null });
                setAssignAllUngroupedDocuments(false);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Create Group
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Document Group</DialogTitle>
                <DialogDescription>Create a new group to organize documents</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" value={formData.name} onChange={(event) => setFormData({ ...formData, name: event.target.value })} required />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea id="description" value={formData.description} onChange={(event) => setFormData({ ...formData, description: event.target.value })} />
                </div>
                <div>
                  <Label htmlFor="position">Position</Label>
                  <Input
                    id="position"
                    type="number"
                    value={formData.position}
                    onChange={(event) => setFormData({ ...formData, position: parseInt(event.target.value, 10) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="parent_id">Parent folder</Label>
                  <select
                    id="parent_id"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.parent_id === null ? '' : String(formData.parent_id)}
                    onChange={(event) => setFormData({ ...formData, parent_id: event.target.value === '' ? null : Number(event.target.value) })}
                  >
                    <option value="">Top level</option>
                    {createParentOptions.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-start space-x-3 rounded-md border p-3">
                  <Checkbox
                    id="assign-all-ungrouped-documents"
                    checked={assignAllUngroupedDocuments}
                    onCheckedChange={(checked) => setAssignAllUngroupedDocuments(checked === true)}
                    disabled={ungroupedCount === 0}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="assign-all-ungrouped-documents" className="cursor-pointer">Assign all ungrouped documents to this group</Label>
                    <p className="text-sm text-muted-foreground">
                      {ungroupedCount > 0
                        ? `${ungroupedCount} ungrouped document(s) will be added after creation.`
                        : 'There are no ungrouped documents to assign right now.'}
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit">Create</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mb-8 space-y-2">
          <h2 className="mb-4 text-2xl font-semibold">Document Groups</h2>
          {groups.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                No document groups yet. Create one to organize your documents.
              </CardContent>
            </Card>
          ) : (
            rootGroups.map((group) => (
              <DocumentGroupBranch
                key={group.id}
                group={group}
                depth={0}
                childrenByParent={childrenByParent}
                expandedGroupIds={expandedGroupIds}
                documentsByGroup={documentsByGroup}
                onToggleDocuments={toggleGroupDocuments}
                onDocumentSearchChange={(groupId, search) => setDocumentsByGroup((previous) => ({
                  ...previous,
                  [groupId]: {
                    documents: previous[groupId]?.documents || [],
                    pagination: previous[groupId]?.pagination || emptyPagination(),
                    search,
                    loading: previous[groupId]?.loading || false
                  }
                }))}
                onDocumentSearchSubmit={(groupId) => loadGroupDocuments(groupId)}
                onDocumentPageChange={(groupId, page) => loadGroupDocuments(groupId, { page })}
                onDocumentPerPageChange={(groupId, perPage) => loadGroupDocuments(groupId, { page: 1, perPage })}
                onEdit={openEditDialog}
                onAssign={openAssignDialog}
                onDelete={handleDelete}
              />
            ))
          )}
        </div>

        <div className="space-y-4">
          <Button
            variant="outline"
            onClick={() => {
              const nextExpanded = !ungroupedExpanded;
              setUngroupedExpanded(nextExpanded);
              if (nextExpanded && ungroupedDocuments.pagination.total_count === 0 && ungroupedDocuments.documents.length === 0) {
                loadUngroupedDocuments();
              }
            }}
          >
            {ungroupedExpanded ? <ChevronDown className="mr-1 h-4 w-4" /> : <ChevronRight className="mr-1 h-4 w-4" />}
            Ungrouped Documents ({ungroupedCount})
          </Button>
          {ungroupedExpanded && (
            <Card>
              <CardContent className="p-6">
                <DocumentList
                  state={ungroupedDocuments}
                  searchPlaceholder="Search ungrouped documents"
                  onSearchChange={(search) => setUngroupedDocuments((previous) => ({ ...previous, search }))}
                  onSearchSubmit={() => loadUngroupedDocuments()}
                  onPageChange={(page) => loadUngroupedDocuments({ page })}
                  onPerPageChange={(perPage) => loadUngroupedDocuments({ page: 1, perPage })}
                />
              </CardContent>
            </Card>
          )}
        </div>

        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Document Group</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Name</Label>
                <Input id="edit-name" value={formData.name} onChange={(event) => setFormData({ ...formData, name: event.target.value })} required />
              </div>
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea id="edit-description" value={formData.description} onChange={(event) => setFormData({ ...formData, description: event.target.value })} />
              </div>
              <div>
                <Label htmlFor="edit-position">Position</Label>
                <Input
                  id="edit-position"
                  type="number"
                  value={formData.position}
                  onChange={(event) => setFormData({ ...formData, position: parseInt(event.target.value, 10) || 0 })}
                />
              </div>
              <div>
                <Label htmlFor="edit-parent_id">Parent folder</Label>
                <select
                  id="edit-parent_id"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.parent_id === null ? '' : String(formData.parent_id)}
                  onChange={(event) => setFormData({ ...formData, parent_id: event.target.value === '' ? null : Number(event.target.value) })}
                >
                  <option value="">Top level</option>
                  {editParentOptions.map((option) => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </select>
              </div>
              <DialogFooter>
                <Button type="submit">Update</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
          <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle>Assign Documents to {selectedGroup?.name}</DialogTitle>
              <DialogDescription>
                Browse municipality documents or search by name or description, then add or remove them from this group.
              </DialogDescription>
            </DialogHeader>

            <form
              className="flex flex-wrap gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                loadAssignableDocuments({ page: 1 });
              }}
            >
              <Input
                className="min-w-0 flex-1"
                value={assignableDocuments.search}
                onChange={(event) => setAssignableDocuments((previous) => ({ ...previous, search: event.target.value }))}
                placeholder="Search municipality documents..."
              />
              <Button type="submit" variant="outline" className="shrink-0">
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
              {assignableDocuments.search.trim() && (
                <Button
                  type="button"
                  variant="ghost"
                  className="shrink-0"
                  onClick={() => {
                    setAssignableDocuments((previous) => ({ ...previous, search: '' }));
                    loadAssignableDocuments({ search: '', page: 1 });
                  }}
                >
                  Clear
                </Button>
              )}
            </form>

            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
              {assignableDocuments.loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading documents
                </div>
              ) : assignableDocuments.documents.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {assignableDocuments.search.trim()
                    ? 'No documents matched your search.'
                    : 'No municipality documents are available to assign.'}
                </p>
              ) : (
                assignableDocuments.documents.map((doc) => {
                  const isAssigning = assigningDocumentIds.has(doc.id);
                  return (
                    <div key={doc.id} className="flex min-w-0 items-center justify-between gap-3 rounded-md border p-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate font-medium">{doc.name}</p>
                          {doc.assigned_to_group && <Badge variant="outline">Assigned</Badge>}
                        </div>
                        <p className="truncate text-sm text-muted-foreground">{doc.description}</p>
                      </div>
                      <Button
                        className="shrink-0"
                        variant={doc.assigned_to_group ? 'outline' : 'default'}
                        size="sm"
                        disabled={isAssigning}
                        onClick={() => handleDocumentAssignment(doc.id, !doc.assigned_to_group)}
                      >
                        {isAssigning && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                        {doc.assigned_to_group ? 'Remove' : 'Add'}
                      </Button>
                    </div>
                  );
                })
              )}
            </div>

            {!assignableDocuments.loading && (
              <PaginatedTableControls
                variant="compact"
                perPageSelectId="assign-documents-per-page"
                currentPage={assignableDocuments.pagination.current_page}
                totalPages={assignableDocuments.pagination.total_pages}
                perPage={assignableDocuments.pagination.per_page}
                totalCount={assignableDocuments.pagination.total_count}
                onPageChange={(page) => loadAssignableDocuments({ page })}
                onPerPageChange={(perPage) => loadAssignableDocuments({ page: 1, perPage })}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
